import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Storefront, Plugs, ArrowsClockwise, CheckCircle, WarningCircle, GoogleLogo, LinkSimple, LinkBreak, Copy, Sparkle } from "@phosphor-icons/react";
import { api, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const inputCls = "rounded-none bg-secondary border-border focus-visible:ring-primary";

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "never";
  const d = new Date(iso);
  return d.toLocaleString();
}

function IntegrationCard({ data, icon: Icon, title, description, roleMapping, children, onChange, saving, onSave, onSync, syncing }) {
  return (
    <div className="border border-border bg-card p-6 flex flex-col" data-testid={`integration-${data.provider}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 border border-border flex items-center justify-center"><Icon size={22} className="text-primary" /></div>
          <div>
            <h3 className="font-head text-lg font-medium">{title}</h3>
            <p className="text-xs text-muted-foreground font-light">{description}</p>
          </div>
        </div>
        <span className={`px-2 py-0.5 border text-[10px] font-mono tracking-widest uppercase ${data.configured ? "text-primary border-primary/40" : "text-muted-foreground border-border"}`}>
          {data.configured ? "Configured" : "Not set"}
        </span>
      </div>

      <div className="mt-6 space-y-4">{children}</div>

      <div className="mt-4 border border-border/60 bg-secondary/40 p-3">
        <p className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">Role mapping</p>
        <p className="text-xs text-muted-foreground font-light mt-1">{roleMapping}</p>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Switch data-testid={`${data.provider}-enabled-switch`} checked={data.enabled} onCheckedChange={(v) => onChange({ enabled: v })} />
          <span className="font-mono text-xs text-muted-foreground">Enabled</span>
        </div>
        <div className="flex items-center gap-2">
          <Switch data-testid={`${data.provider}-autosync-switch`} checked={data.auto_sync} onCheckedChange={(v) => onChange({ auto_sync: v })} />
          <span className="font-mono text-xs text-muted-foreground">Auto-sync (15 min)</span>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-border flex items-center gap-3">
        <Button data-testid={`${data.provider}-save-button`} onClick={onSave} disabled={saving} className="rounded-none bg-secondary hover:bg-accent border border-border text-foreground font-mono text-xs tracking-wide">
          {saving ? "Saving…" : "Save settings"}
        </Button>
        <Button data-testid={`${data.provider}-sync-button`} onClick={onSync} disabled={syncing || !data.configured} className="rounded-none bg-primary hover:bg-primary/90 font-mono text-xs tracking-wide">
          <ArrowsClockwise size={16} className={`mr-2 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync now"}
        </Button>
      </div>

      <div className="mt-4 flex items-center gap-2 font-mono text-[11px]">
        {data.last_error ? (
          <><WarningCircle size={14} className="text-destructive" /><span className="text-destructive truncate" title={data.last_error}>{data.last_error}</span></>
        ) : data.last_sync ? (
          <><CheckCircle size={14} className="text-primary" /><span className="text-muted-foreground">Last sync: {timeAgo(data.last_sync)} · {data.last_count ?? 0} contacts</span></>
        ) : (
          <span className="text-muted-foreground">Not synced yet</span>
        )}
      </div>
    </div>
  );
}

export default function IntegrationsPanel() {
  const qc = useQueryClient();
  const { data: integrations = [] } = useQuery({ queryKey: ["integrations"], queryFn: async () => (await api.get("/admin/integrations")).data });
  const [drafts, setDrafts] = useState({});

  useEffect(() => {
    setDrafts((prev) =>
      Object.fromEntries(integrations.map((i) => [i.provider, { ...i, _secrets: prev[i.provider]?._secrets || {} }]))
    );
  }, [integrations]);

  const saveMut = useMutation({
    mutationFn: async ({ provider, body }) => api.put(`/admin/integrations/${provider}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["integrations"] }); toast.success("Integration settings saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });
  const syncMut = useMutation({
    mutationFn: async (provider) => api.post(`/admin/integrations/${provider}/sync`),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ["integrations"] }); qc.invalidateQueries({ queryKey: ["admin-users"] }); toast.success(`Synced ${res.data.synced_count} contacts`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Sync failed"),
  });

  const upd = (provider, patch) => setDrafts((p) => ({ ...p, [provider]: { ...p[provider], ...patch } }));
  const updSecret = (provider, key, val) => setDrafts((p) => ({ ...p, [provider]: { ...p[provider], _secrets: { ...p[provider]?._secrets, [key]: val } } }));

  const save = (provider) => {
    const d = drafts[provider] || {};
    const body = { enabled: d.enabled, auto_sync: d.auto_sync, ...(d._secrets || {}) };
    if (provider === "woocommerce") body.store_url = d.store_url || "";
    if (provider === "claude") body.model = d.model || "claude-sonnet-5";
    saveMut.mutate({ provider, body });
  };

  const woo = drafts["woocommerce"];
  const hub = drafts["hubspot"];
  const gmail = drafts["gmail"];
  const claude = drafts["claude"];

  const disconnectMut = useMutation({
    mutationFn: async () => api.post("/admin/gmail/disconnect"),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["integrations"] }); toast.success("Google account disconnected"); },
  });
  const redirectUri = `${API}/oauth/gmail/callback`;
  const connectGoogle = () => {
    const t = localStorage.getItem("np_token");
    window.location.href = `${API}/oauth/gmail/start?token=${t}`;
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="integrations-panel">
      {woo && (
        <IntegrationCard
          data={woo} icon={Storefront} title="WooCommerce" description="Import store customers as contacts"
          roleMapping="All WooCommerce customers → role: Customer · status: Active"
          onChange={(patch) => upd("woocommerce", patch)} onSave={() => save("woocommerce")} saving={saveMut.isPending}
          onSync={() => syncMut.mutate("woocommerce")} syncing={syncMut.isPending && syncMut.variables === "woocommerce"}
        >
          <Field label="Store URL">
            <Input data-testid="woo-url-input" className={inputCls} placeholder="https://nettpilot.no" value={woo.store_url || ""} onChange={(e) => upd("woocommerce", { store_url: e.target.value })} />
          </Field>
          <Field label={`Consumer Key ${woo.consumer_key_set ? "(saved — leave blank to keep)" : ""}`}>
            <Input data-testid="woo-key-input" className={inputCls} placeholder="ck_…" value={woo._secrets?.consumer_key || ""} onChange={(e) => updSecret("woocommerce", "consumer_key", e.target.value)} />
          </Field>
          <Field label={`Consumer Secret ${woo.consumer_secret_set ? "(saved — leave blank to keep)" : ""}`}>
            <Input data-testid="woo-secret-input" type="password" className={inputCls} placeholder="cs_…" value={woo._secrets?.consumer_secret || ""} onChange={(e) => updSecret("woocommerce", "consumer_secret", e.target.value)} />
          </Field>
        </IntegrationCard>
      )}

      {hub && (
        <IntegrationCard
          data={hub} icon={Plugs} title="HubSpot" description="Import CRM contacts with lifecycle stage"
          roleMapping="customer → Customer · evangelist/partner → Partner · MQL/SQL/opportunity → Lead (Prospect) · else → Lead (New)"
          onChange={(patch) => upd("hubspot", patch)} onSave={() => save("hubspot")} saving={saveMut.isPending}
          onSync={() => syncMut.mutate("hubspot")} syncing={syncMut.isPending && syncMut.variables === "hubspot"}
        >
          <Field label={`Private App Token ${hub.access_token_set ? "(saved — leave blank to keep)" : ""}`}>
            <Input data-testid="hub-token-input" type="password" className={inputCls} placeholder="pat-na1-…" value={hub._secrets?.access_token || ""} onChange={(e) => updSecret("hubspot", "access_token", e.target.value)} />
          </Field>
          <p className="text-xs text-muted-foreground font-light">
            Create a Private App in HubSpot → Settings → Integrations → Private Apps with the <span className="font-mono text-foreground">crm.objects.contacts.read</span> scope.
          </p>
        </IntegrationCard>
      )}

      {gmail && (
        <div className="border border-border bg-card p-6 flex flex-col lg:col-span-2" data-testid="integration-gmail">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 border border-border flex items-center justify-center"><GoogleLogo size={22} className="text-primary" /></div>
              <div>
                <h3 className="font-head text-lg font-medium">Gmail / Google Workspace</h3>
                <p className="text-xs text-muted-foreground font-light">Send from your own domain + log emails to the activity timeline</p>
              </div>
            </div>
            <span className={`px-2 py-0.5 border text-[10px] font-mono tracking-widest uppercase ${gmail.connected ? "text-primary border-primary/40" : "text-muted-foreground border-border"}`}>
              {gmail.connected ? "Connected" : gmail.configured ? "Not connected" : "Not set"}
            </span>
          </div>

          {gmail.connected ? (
            <>
              <div className="mt-6 flex items-center justify-between border border-primary/30 bg-primary/5 p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle size={22} className="text-primary" />
                  <div>
                    <p className="text-sm font-medium">{gmail.connected_email}</p>
                    <p className="font-mono text-[11px] text-muted-foreground">Ready to send &amp; sync emails</p>
                  </div>
                </div>
                <Button data-testid="gmail-disconnect-button" onClick={() => disconnectMut.mutate()} className="rounded-none bg-secondary border border-border text-foreground hover:bg-accent font-mono text-xs tracking-wide">
                  <LinkBreak size={16} className="mr-2" /> Disconnect
                </Button>
              </div>
              <div className="mt-4 flex items-center justify-between border border-border p-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Switch data-testid="gmail-autosync-switch" checked={!!gmail.auto_sync} onCheckedChange={(v) => saveMut.mutate({ provider: "gmail", body: { auto_sync: v } })} />
                    <span className="font-mono text-xs text-muted-foreground">Auto-sync every contact's inbox hourly</span>
                  </div>
                  <p className="font-mono text-[11px] mt-2">
                    {gmail.last_error ? (
                      <span className="text-destructive">{gmail.last_error}</span>
                    ) : gmail.last_sync ? (
                      <span className="text-muted-foreground">Last auto-sync: {new Date(gmail.last_sync).toLocaleString()} · {gmail.last_count ?? 0} imported</span>
                    ) : (
                      <span className="text-muted-foreground">No auto-sync run yet</span>
                    )}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="mt-6 grid md:grid-cols-2 gap-4">
                <Field label={`Google Client ID ${gmail.google_client_id_set ? "(saved)" : ""}`}>
                  <Input data-testid="gmail-client-id-input" className={inputCls} placeholder="…apps.googleusercontent.com" value={gmail._secrets?.google_client_id || ""} onChange={(e) => updSecret("gmail", "google_client_id", e.target.value)} />
                </Field>
                <Field label={`Google Client Secret ${gmail.google_client_secret_set ? "(saved — leave blank to keep)" : ""}`}>
                  <Input data-testid="gmail-client-secret-input" type="password" className={inputCls} placeholder="GOCSPX-…" value={gmail._secrets?.google_client_secret || ""} onChange={(e) => updSecret("gmail", "google_client_secret", e.target.value)} />
                </Field>
              </div>
              <div className="mt-4 border border-border/60 bg-secondary/40 p-3">
                <p className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Add this Authorized redirect URI in Google Cloud</p>
                <div className="flex items-center gap-2">
                  <code className="text-[11px] break-all text-foreground">{redirectUri}</code>
                  <button onClick={() => { navigator.clipboard?.writeText(redirectUri); toast.success("Copied"); }} className="text-muted-foreground hover:text-primary shrink-0" data-testid="gmail-copy-redirect"><Copy size={14} /></button>
                </div>
                <p className="text-xs text-muted-foreground font-light mt-2">Enable the Gmail API, add scopes gmail.send + gmail.readonly, add yourself as a test user. Then save your keys and connect.</p>
              </div>
              <div className="mt-5 pt-4 border-t border-border flex items-center gap-3">
                <Button data-testid="gmail-save-button" onClick={() => save("gmail")} disabled={saveMut.isPending} className="rounded-none bg-secondary hover:bg-accent border border-border text-foreground font-mono text-xs tracking-wide">
                  {saveMut.isPending ? "Saving…" : "Save keys"}
                </Button>
                <Button data-testid="gmail-connect-button" onClick={connectGoogle} disabled={!gmail.configured} className="rounded-none bg-primary hover:bg-primary/90 font-mono text-xs tracking-wide">
                  <LinkSimple size={16} className="mr-2" /> Connect Google
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {claude && (
        <div className="border border-border bg-card p-6 flex flex-col lg:col-span-2" data-testid="integration-claude">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 border border-border flex items-center justify-center"><Sparkle size={22} className="text-primary" /></div>
              <div>
                <h3 className="font-head text-lg font-medium">Claude AI assistant</h3>
                <p className="text-xs text-muted-foreground font-light">Draft emails, summarize contacts, explain 3LA scores &amp; write library content</p>
              </div>
            </div>
            <span className={`px-2 py-0.5 border text-[10px] font-mono tracking-widest uppercase ${claude.enabled ? "text-primary border-primary/40" : "text-muted-foreground border-border"}`}>
              {claude.enabled ? "On" : "Off"}
            </span>
          </div>
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <Field label={`Anthropic API key ${claude.anthropic_api_key_set ? "(saved — leave blank to keep)" : "(optional)"}`}>
              <Input data-testid="claude-key-input" type="password" className={inputCls} placeholder="sk-ant-… (blank = use built-in Emergent key)" value={claude._secrets?.anthropic_api_key || ""} onChange={(e) => updSecret("claude", "anthropic_api_key", e.target.value)} />
            </Field>
            <Field label="Model">
              <select data-testid="claude-model-select" className={`${inputCls} h-10 w-full px-3 text-sm`} value={claude.model || "claude-sonnet-5"} onChange={(e) => upd("claude", { model: e.target.value })}>
                <option value="claude-sonnet-5">Claude Sonnet 5 (recommended)</option>
                <option value="claude-opus-4-8">Claude Opus 4.8 (most capable)</option>
                <option value="claude-haiku-4-5-20251001">Claude Haiku 4.5 (fastest)</option>
                <option value="claude-sonnet-4-6">Claude Sonnet 4.6</option>
              </select>
            </Field>
          </div>
          <div className="mt-4 border border-border/60 bg-secondary/40 p-3">
            <p className="text-xs text-muted-foreground font-light">
              {claude.uses_emergent_key
                ? "Currently using the built-in Emergent key (usage draws from your Emergent balance). Add your own Anthropic key above to bill Claude directly."
                : "Using your own Anthropic API key."}
            </p>
          </div>
          <div className="mt-5 pt-4 border-t border-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Switch data-testid="claude-enabled-switch" checked={!!claude.enabled} onCheckedChange={(v) => upd("claude", { enabled: v })} />
              <span className="font-mono text-xs text-muted-foreground">Enable AI features</span>
            </div>
            <Button data-testid="claude-save-button" onClick={() => save("claude")} disabled={saveMut.isPending} className="rounded-none bg-primary hover:bg-primary/90 font-mono text-xs tracking-wide">
              {saveMut.isPending ? "Saving…" : "Save settings"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
